import clsx from "clsx";
import {
  FunctionComponent,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import type { EditorNoteBlock } from "@story/data-centers-hub/config/editorNoteBlocks";
import { introWipeMs } from "@story/data-centers-hub/config/intro";
import { resolveHubSlideLink } from "@story/data-centers-hub/config/resolveHubSlideLink";
import type {
  DeckSlideId,
  GridCellPlacement,
} from "@story/data-centers-hub/config/slides";
import type { HubStateOutlineId } from "@story/data-centers-hub/config/stateOutlines";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";

import { NoteBlocksColumn } from "./NoteBlocks";
import * as styles from "./IntroCovers.css";

/** Chapter cards that can hold an accent through a deeplink wipe. */
function outlineForSlide(id: DeckSlideId): HubStateOutlineId | null {
  if (id === "virginia" || id === "utah" || id === "arizona") {
    return id;
  }
  return null;
}

type WipeDir = "up" | "down" | "left" | "right";

const OUT: Record<WipeDir, string> = {
  up: styles.outUp,
  down: styles.outDown,
  left: styles.outLeft,
  right: styles.outRight,
};

/** Per-cell wipe direction. */
const WIPE_DIR: Record<string, WipeDir> = {
  tl: "left",
  tm: "down",
  tr: "left",
  "bl-merged": "up",
  br: "down",
};

/**
 * Stage-sized frames inside each cover. The grid matches the title grid.
 * Covers bleed 2px for Safari/Firefox hairlines; the note clip sits 1px in
 * so these offsets (still keyed to that 1px) put every copy on the stage
 * origin. `cqw`/`cqh` are the layer.
 */
const NOTE_CLIP_BLEED_PX = 1;
const NOTE_FRAME: Record<string, CSSProperties> = {
  tl: { left: "0px", top: "0px" },
  tm: {
    left: `calc(-100cqw / 3 + ${NOTE_CLIP_BLEED_PX}px / 3)`,
    top: "0px",
  },
  tr: {
    left: `calc(-200cqw / 3 + ${NOTE_CLIP_BLEED_PX * 2}px / 3)`,
    top: "0px",
  },
  "bl-merged": {
    left: "0px",
    top: `calc(-50cqh + ${NOTE_CLIP_BLEED_PX / 2}px)`,
  },
  br: {
    left: `calc(-200cqw / 3 + ${NOTE_CLIP_BLEED_PX * 2}px / 3)`,
    top: `calc(-50cqh + ${NOTE_CLIP_BLEED_PX / 2}px)`,
  },
};

/** Utah gallery chevron, pointed down. */
const SCROLL_CHEVRON_PATH = "M5 9 L12 16 L19 9";

export type IntroPhase = "hold" | "wiping" | "returning" | "done";

interface Props {
  cells: ReadonlyArray<GridCellPlacement>;
  blocks: ReadonlyArray<EditorNoteBlock>;
  phase: IntroPhase;
  /** Snap the covers away with no shift. */
  motionOff: boolean;
  /** Begin the cover wipe. Optional landing skips the title (e.g. Loudoun). */
  onStart: (landing?: DeckSlideId) => void;
  onDone: () => void;
  /** Squares have slid back; the note can scroll again. */
  onSettled: () => void;
  /**
   * In-note chapter link. Prefer this over `onStart(landing)` so mid-wipe
   * locks and motion-off scroll still match `SlideDeck.goTo`.
   */
  onSlideLink?: (id: DeckSlideId) => void;
  /**
   * Scroll offset from the last time the note wiped away. The component
   * unmounts while the title is up, so the parent keeps this.
   */
  restoredScroll?: number;
  /** Report the scroller offset as the wipe starts, so a return can restore it. */
  onNoteScroll?: (top: number) => void;
}

function ArrowIcon() {
  return (
    <svg className={styles.scrollCueIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path className={styles.scrollCuePath} d={SCROLL_CHEVRON_PATH} />
    </svg>
  );
}

function wheelPixels(event: WheelEvent): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return event.deltaY * 16;
  }
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return event.deltaY * window.innerHeight;
  }
  return event.deltaY;
}

/**
 * Y where the note bottom gates the handoff — 87.5% down the viewport
 * (12.5% up from the bottom), desktop and narrow. Prefer `visualViewport`
 * so the line tracks the same box as CSS `svh` (Brave/Android chrome).
 */
function wipeLineY(): number {
  const h = window.visualViewport?.height ?? window.innerHeight;
  return h * 0.875;
}

/**
 * Extra down-scroll (px) past the wipe line before the intro wipe runs.
 * Friction on the same gesture — not a second swipe. Narrow is shorter so a
 * phone flick that reaches the line can still finish the budget.
 */
const COMMIT_LEAVE_PX = 180;
const COMMIT_LEAVE_PX_NARROW = 72;

/**
 * After a *wheel* stream first hits the wipe line, ignore down-deltas for
 * this long so trackpad synthetic inertia cannot spend the commit budget.
 * Touch must not use this — finger moves are intentional, and `measure()`
 * arming the gate used to start an eat window that ate the next swipe.
 */
const INERTIA_EAT_MS = 160;

/**
 * Launch covers: opaque black squares matched to the 3×2 grid. The note lives
 * in an in-slide scroller. Reaching the wipe line gates leave (cue bobs);
 * further down-scroll accumulates until `COMMIT_LEAVE_PX`, then the wipe
 * runs. Desktop: each slot clips a note copy as its square shifts. Narrow:
 * no copies — title cells wipe over the one scroller on a solid black sheet.
 */
const IntroCovers: FunctionComponent<Props> = ({
  cells,
  blocks,
  phase,
  motionOff,
  onStart,
  onDone,
  onSettled,
  onSlideLink,
  restoredScroll = 0,
  onNoteScroll,
}) => {
  const narrow = useHubNarrowViewport();
  const completedRef = useRef(false);
  const startedRef = useRef(false);
  const layerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const columnRef = useRef<HTMLDivElement>(null);
  const tailRef = useRef<HTMLDivElement>(null);
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const onNoteScrollRef = useRef(onNoteScroll);
  onNoteScrollRef.current = onNoteScroll;
  const onSlideLinkRef = useRef(onSlideLink);
  onSlideLinkRef.current = onSlideLink;
  // Desktop squares start off-screen when we resume mid-deck (`done`) or mid
  // return — a hard refresh below the intro used to mount `done` with squares
  // in (covering), so title→intro had nothing to animate back.
  // Mobile: no fragment return — squares stay covering; title cells reverse.
  const [squaresOut, setSquaresOut] = useState(
    () =>
      !narrow && (phase === "returning" || phase === "done"),
  );
  /** Chapter card accent latched for the cover wipe (hover dies on pointer-up). */
  const [armedState, setArmedState] = useState<HubStateOutlineId | null>(null);
  /**
   * Mobile wipe/return: under the stage so title cells / chapter clips can
   * run over the note. Derived (not effect state) — a post-paint
   * `setUnderStage(true)` left one returning frame at z-index 20 and covered
   * the lvh ballast strip (read as an unmount) before the wipe started.
   */
  const underStage = narrow && (phase === "wiping" || phase === "returning");

  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setPortalRoot(document.body);
  }, []);
  /** Note is taller than the stage, so the in-slide scroller is active. */
  const [needsScroll, setNeedsScroll] = useState(false);
  const [showFade, setShowFade] = useState(false);
  /** At wipe line — cue bobs; down-scroll commits toward leave. */
  const [gated, setGated] = useState(false);
  const needsScrollRef = useRef(needsScroll);
  needsScrollRef.current = needsScroll;
  const gatedRef = useRef(false);
  /** `scrollTop` when the gate armed — used to ignore chrome-resize ungates. */
  const gatedAtScrollRef = useRef(0);
  const commitPxRef = useRef(0);
  /** `performance.now()` until which down-deltas are discarded (inertia eat). */
  const inertiaUntilRef = useRef(0);
  const measureRef = useRef<() => void>(() => {});
  const scrollMeasureRaf = useRef(0);

  const noteScrollRef = useRef(restoredScroll);
  const initialScrollRef = useRef(restoredScroll);
  /** Tail mounts a commit after we learn the note overflows; keep applying until then. */
  const restorePendingRef = useRef(restoredScroll > 0);
  const restoreTriesRef = useRef(0);
  const lastScrollTopRef = useRef(restoredScroll);

  const setGatedState = (next: boolean) => {
    gatedRef.current = next;
    if (!next) {
      commitPxRef.current = 0;
      inertiaUntilRef.current = 0;
      gatedAtScrollRef.current = 0;
    }
    setGated((current) => (current === next ? current : next));
  };

  const commitLeavePx = () =>
    narrow ? COMMIT_LEAVE_PX_NARROW : COMMIT_LEAVE_PX;

  const enterGate = (eatWheelInertia = false) => {
    if (gatedRef.current) {
      return;
    }
    commitPxRef.current = 0;
    gatedAtScrollRef.current = scrollerRef.current?.scrollTop ?? 0;
    inertiaUntilRef.current = eatWheelInertia
      ? performance.now() + INERTIA_EAT_MS
      : 0;
    setGatedState(true);
  };

  const clearGate = () => {
    setGatedState(false);
  };

  const eatingInertia = () => performance.now() < inertiaUntilRef.current;

  const endInertiaEat = () => {
    inertiaUntilRef.current = 0;
  };

  /** Keep the live scroller and square-copy shift on the same latched depth. */
  const syncNoteScroll = (top: number) => {
    const next = Math.max(0, top);
    noteScrollRef.current = next;
    lastScrollTopRef.current = next;
    const scroller = scrollerRef.current;
    if (scroller && Math.abs(scroller.scrollTop - next) > 0.5) {
      scroller.scrollTop = next;
    }
    if (next) {
      layerRef.current?.style.setProperty("--note-scroll", `${-next}px`);
    } else {
      layerRef.current?.style.removeProperty("--note-scroll");
    }
  };

  const startWipe = (landing?: DeckSlideId) => {
    if (startedRef.current) {
      return;
    }
    const top = scrollerRef.current?.scrollTop ?? 0;
    // A zero translation still promotes a text layer and shifts the glyphs
    // a pixel against the scroller copy. Only transform once we've scrolled.
    syncNoteScroll(top);
    onNoteScrollRef.current?.(top);
    startedRef.current = true;
    restorePendingRef.current = false;
    clearGate();
    setShowFade(false);
    onStartRef.current(landing);
  };

  /**
   * Down intent past the wipe line — accumulate until commit threshold.
   * `eatWheelInertia` only for wheel/trackpad (synthetic fling). Touch
   * deltas always count.
   */
  const addCommit = (deltaPx: number, eatWheelInertia = false) => {
    if (startedRef.current || deltaPx <= 0) {
      return;
    }
    enterGate(eatWheelInertia);
    if (eatingInertia()) {
      return;
    }
    commitPxRef.current += deltaPx;
    if (commitPxRef.current >= commitLeavePx()) {
      startWipe();
    }
  };

  /** Chapter links in the WP note — cover wipe lands on that stop. */
  const onNoteClick = (event: ReactMouseEvent<HTMLElement>) => {
    if (phase !== "hold" || startedRef.current) {
      return;
    }
    const anchor = (event.target as Element | null)?.closest?.("a");
    if (!(anchor instanceof HTMLAnchorElement)) {
      return;
    }
    const href = anchor.getAttribute("href");
    if (!href) {
      return;
    }
    const slideId = resolveHubSlideLink(href);
    if (!slideId || slideId === "note") {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    // Narrow only — desktop square wipe feels wrong with a stuck fill.
    if (narrow) {
      setArmedState(outlineForSlide(slideId));
    }
    // Same scroll latch as `startWipe`, then hand the hop to the deck so
    // busy lock / chapter mount match PackageNav skips.
    const top = scrollerRef.current?.scrollTop ?? 0;
    syncNoteScroll(top);
    onNoteScrollRef.current?.(top);
    startedRef.current = true;
    restorePendingRef.current = false;
    clearGate();
    setShowFade(false);
    if (onSlideLinkRef.current) {
      onSlideLinkRef.current(slideId);
      return;
    }
    onStartRef.current(slideId);
  };

  const continueNote = () => {
    const scroller = scrollerRef.current;
    const column = columnRef.current;
    // Explicit click: leave if already gated / short note; otherwise scroll
    // to the wipe line (gate + bob), next click or commit-scroll leaves.
    if (!scroller || !column || !needsScrollRef.current) {
      startWipe();
      return;
    }
    const room = column.getBoundingClientRect().bottom - wipeLineY();
    if (room <= 0) {
      startWipe();
      return;
    }
    scroller.scrollTo({
      top: scroller.scrollTop + room,
      behavior: "smooth",
    });
  };

  // Put the scroller back before paint. Copies read the same offset, so the
  // return and the note that appears at the end of it show the same lines.
  useLayoutEffect(() => {
    if (!restorePendingRef.current) {
      return;
    }
    const top = initialScrollRef.current;
    const scroller = scrollerRef.current;
    if (!scroller || top <= 0) {
      restorePendingRef.current = false;
      return;
    }
    syncNoteScroll(top);
    restoreTriesRef.current += 1;
    const reached = scroller.scrollTop >= top - 1;
    if (!reached && restoreTriesRef.current < 8) {
      return;
    }
    restorePendingRef.current = false;
  });

  // `visibility: hidden` on the scroller (and the done layer) can zero
  // `scrollTop` in some engines while `--note-scroll` still shifts the square
  // copies. Re-apply the latch before hold/return paint so the handoff does
  // not snap the note down.
  useLayoutEffect(() => {
    if (phase !== "hold" && phase !== "returning") {
      return;
    }
    syncNoteScroll(noteScrollRef.current);
  }, [phase]);

  const measure = () => {
    const scroller = scrollerRef.current;
    const column = columnRef.current;
    if (!scroller || !column) {
      return;
    }
    const tailHeight = tailRef.current?.offsetHeight ?? 0;
    const contentHeight = scroller.scrollHeight - tailHeight;
    const overflows = contentHeight > scroller.clientHeight + 1;
    setNeedsScroll((current) => (current === overflows ? current : overflows));
    if (phase !== "hold") {
      setShowFade(false);
      return;
    }
    const textBottom = column.getBoundingClientRect().bottom;
    const viewBottom = scroller.getBoundingClientRect().bottom;
    const fade = overflows && textBottom > viewBottom + 1;
    setShowFade((current) => (current === fade ? current : fade));
    const scrollTop = scroller.scrollTop;
    // Keep depth latched during hold — PackageNav wipe hides the scroller
    // (`visibility: hidden`) which zeros `scrollTop` before a post-paint latch.
    noteScrollRef.current = scrollTop;
    lastScrollTopRef.current = scrollTop;
    const line = wipeLineY();
    if (textBottom > line) {
      // Brave (and Android Chrome) resize the visual viewport when the URL bar
      // toggles — ResizeObserver remeasures, the wipe line jumps, and clearing
      // the gate here wiped the leave budget mid-gesture. Only drop the gate
      // when the user has scrolled back up from the arm depth.
      if (
        gatedRef.current &&
        scrollTop < gatedAtScrollRef.current - 1
      ) {
        clearGate();
      }
      return;
    }
    // Long note at the wipe line, or short note that already fits — cue bobs;
    // further down-scroll still has to cover the commit budget. No inertia
    // eat here — we are not inside a wheel fling.
    enterGate(false);
  };
  measureRef.current = measure;

  const scheduleMeasure = () => {
    if (scrollMeasureRaf.current !== 0) {
      return;
    }
    scrollMeasureRaf.current = requestAnimationFrame(() => {
      scrollMeasureRaf.current = 0;
      measureRef.current();
    });
  };

  useEffect(
    () => () => {
      cancelAnimationFrame(scrollMeasureRaf.current);
    },
    [],
  );

  useLayoutEffect(() => {
    // Wipe frames re-render the note. Measuring then forces layout on the
    // animation. Hold (and scroll restore) is the only time the geometry matters.
    if (phase !== "hold" && !restorePendingRef.current) {
      return;
    }
    measure();
  });

  useEffect(() => {
    if (phase !== "hold") {
      return;
    }
    const scroller = scrollerRef.current;
    if (!scroller || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      measureRef.current();
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [phase, blocks]);

  useEffect(() => {
    if (phase !== "hold") {
      return;
    }
    // Wheel events repeat faster than scrollTop moves. Reuse the last
    // column bottom until the scroller actually shifts.
    let laidOut: { scrollTop: number; bottom: number } | null = null;
    const columnBottom = (
      scroller: HTMLDivElement,
      column: HTMLDivElement,
    ): number => {
      if (laidOut && laidOut.scrollTop === scroller.scrollTop) {
        return laidOut.bottom;
      }
      const bottom = column.getBoundingClientRect().bottom;
      laidOut = { scrollTop: scroller.scrollTop, bottom };
      return bottom;
    };

    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        return;
      }
      const delta = wheelPixels(event);
      if (delta === 0) {
        return;
      }
      const scroller = scrollerRef.current;
      const column = columnRef.current;
      if (!scroller || !column) {
        return;
      }
      if (!needsScrollRef.current) {
        if (delta > 0) {
          event.preventDefault();
          addCommit(delta, true);
        } else if (scroller.scrollTop <= 0) {
          event.preventDefault();
        }
        return;
      }
      const bottom = columnBottom(scroller, column);
      const line = wipeLineY();
      if (delta > 0 && bottom - delta <= line) {
        event.preventDefault();
        const room = Math.max(0, bottom - line);
        if (room > 0) {
          scroller.scrollTop += room;
        }
        // Only the overscroll past the line counts toward leave.
        addCommit(Math.max(0, delta - room), true);
        return;
      }
      if (delta < 0) {
        if (gatedRef.current) {
          // Reverse is intentional — stop eating so the next push can commit.
          endInertiaEat();
          commitPxRef.current = Math.max(0, commitPxRef.current + delta);
          if (commitPxRef.current <= 0 && bottom > line) {
            clearGate();
          }
        }
        if (scroller.scrollTop <= 0) {
          event.preventDefault();
        }
      }
    };
    let lastY: number | null = null;
    const onTouchStart = (event: TouchEvent) => {
      lastY = event.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (event: TouchEvent) => {
      const y = event.touches[0]?.clientY;
      if (lastY == null || y == null) {
        return;
      }
      const delta = lastY - y;
      lastY = y;
      if (Math.abs(delta) <= 8) {
        return;
      }
      if (delta <= 0) {
        if (gatedRef.current && delta < 0) {
          endInertiaEat();
          commitPxRef.current = Math.max(0, commitPxRef.current + delta);
        }
        return;
      }
      const column = columnRef.current;
      const scroller = scrollerRef.current;
      if (!needsScrollRef.current) {
        event.preventDefault();
        addCommit(delta);
        return;
      }
      if (
        column &&
        scroller &&
        columnBottom(scroller, column) <= wipeLineY()
      ) {
        event.preventDefault();
        addCommit(delta);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest(
          'button, a, input, textarea, select, [contenteditable="true"]',
        )
      ) {
        return;
      }
      const scroller = scrollerRef.current;
      if (!scroller) {
        return;
      }
      const down =
        event.key === "ArrowDown" ||
        event.key === "PageDown" ||
        event.key === " ";
      const up = event.key === "ArrowUp" || event.key === "PageUp";
      if (!down && !up) {
        return;
      }
      event.preventDefault();
      const distance =
        event.key === "ArrowDown" || event.key === "ArrowUp"
          ? 48
          : scroller.clientHeight * 0.8;
      if (!needsScrollRef.current) {
        if (down) {
          addCommit(distance);
        }
        return;
      }
      const column = columnRef.current;
      if (
        down &&
        column &&
        columnBottom(scroller, column) <= wipeLineY()
      ) {
        addCommit(distance);
        return;
      }
      if (up && gatedRef.current) {
        endInertiaEat();
        commitPxRef.current = Math.max(0, commitPxRef.current - distance);
      }
      scroller.scrollBy({ top: down ? distance : -distance });
    };
    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [phase, narrow]);

  // Parent-driven wipe (PackageNav / goTo) never calls `startWipe`. Latch in
  // layout so `--note-scroll` is set before paint — the scroller is already
  // `visibility: hidden` this frame and may have lost `scrollTop`.
  useLayoutEffect(() => {
    if (phase !== "wiping" && phase !== "done") {
      return;
    }
    if (phase === "wiping" && !startedRef.current) {
      const live = scrollerRef.current?.scrollTop ?? 0;
      const top = live > 0.5 ? live : noteScrollRef.current;
      syncNoteScroll(top);
      onNoteScrollRef.current?.(top);
      startedRef.current = true;
      restorePendingRef.current = false;
      clearGate();
      setShowFade(false);
      return;
    }
    // Hidden scroller: keep writing the latch so engines that zero scrollTop
    // cannot drift from the square-copy `--note-scroll` shift.
    syncNoteScroll(noteScrollRef.current);
  }, [phase]);

  useEffect(() => {
    if (phase !== "wiping" || completedRef.current) {
      return;
    }
    if (motionOff) {
      completedRef.current = true;
      onDone();
      return;
    }

    // Mobile: keep squares put; `underStage` follows phase. Title cells wipe in.
    if (narrow) {
      setSquaresOut(false);
      const wipeTimer = window.setTimeout(() => {
        completedRef.current = true;
        onDone();
      }, introWipeMs(narrow));
      return () => window.clearTimeout(wipeTimer);
    }

    let wipeTimer = 0;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setSquaresOut(true);
        wipeTimer = window.setTimeout(() => {
          completedRef.current = true;
          onDone();
        }, introWipeMs(narrow));
      });
    });

    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      window.clearTimeout(wipeTimer);
    };
  }, [phase, motionOff, narrow, onDone]);

  // Mid-deck resume can jump hold→done without a wipe; keep squares out so a
  // later title→intro return still has a slide to play.
  useLayoutEffect(() => {
    if (narrow || motionOff || phase !== "done") {
      return;
    }
    setSquaresOut(true);
    completedRef.current = true;
    startedRef.current = true;
  }, [phase, narrow, motionOff]);

  useEffect(() => {
    if (phase !== "returning") {
      return;
    }
    // Allow a later continue / wipe after this return settles.
    completedRef.current = false;
    const armReturnHold = () => {
      startedRef.current = false;
      syncNoteScroll(noteScrollRef.current);
      // Fully scrolled note → gated with empty commit (push again to leave).
      measureRef.current();
      onSettledRef.current();
    };

    if (motionOff) {
      setSquaresOut(false);
      armReturnHold();
      return;
    }

    // Mobile: `underStage` follows phase; title cells / chapter clips close over it.
    if (narrow) {
      setSquaresOut(false);
      const settleTimer = window.setTimeout(() => {
        armReturnHold();
      }, introWipeMs(narrow));
      return () => window.clearTimeout(settleTimer);
    }

    let settleTimer = 0;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setSquaresOut(false);
        settleTimer = window.setTimeout(() => {
          armReturnHold();
        }, introWipeMs(narrow));
      });
    });

    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      window.clearTimeout(settleTimer);
    };
  }, [phase, motionOff, narrow]);

  // Drop the deeplink accent once the wipe (or return) has finished.
  useEffect(() => {
    if (phase === "done" || phase === "returning") {
      setArmedState(null);
    }
  }, [phase]);

  // Keep the note tree mounted through `done` so chapter hops do not reset
  // scroll depth or state-outline draw. Hidden via `[data-hub-intro=done]`.
  if (blocks.length === 0) {
    return null;
  }

  const introAttr =
    phase === "wiping"
      ? "wiping"
      : phase === "done"
        ? "done"
        : phase === "returning"
          ? "returning"
          : "hold";

  const layer = (
    <div
      ref={layerRef}
      className={styles.layer}
      {...(narrow ? { "data-narrow": "" } : {})}
      {...(underStage ? { "data-under-stage": "" } : {})}
      data-hub-intro={introAttr}
      data-note-long={needsScroll ? "true" : "false"}
      style={
        {
          ...(noteScrollRef.current
            ? { "--note-scroll": `${-noteScrollRef.current}px` }
            : null),
        } as CSSProperties
      }
    >
      {!narrow ? (
        <div className={styles.grid} aria-hidden="true">
          {cells.map((cell) => {
            const dir = WIPE_DIR[cell.id] ?? "up";
            const placement: CSSProperties = {
              gridColumn: cell.column,
              gridRow: cell.row,
            };
            return (
              <div
                key={cell.id}
                className={styles.slot}
                style={placement}
                data-intro-cell={cell.id}
              >
                <div
                  className={clsx(styles.cover, squaresOut && OUT[dir])}
                  style={{ transitionDuration: `${introWipeMs(narrow)}ms` }}
                >
                  <div className={styles.clip}>
                    <div className={styles.noteFrame} style={NOTE_FRAME[cell.id]}>
                      <NoteBlocksColumn
                        blocks={blocks}
                        animateOutlines={false}
                        armedState={armedState}
                        className={clsx(
                          noteScrollRef.current !== 0 && styles.noteShift,
                          phase === "hold" && styles.noteHeld,
                        )}
                        cue={
                          <div
                            className={clsx(
                              styles.flowCue,
                              gated && styles.flowCueGated,
                            )}
                          >
                            <ArrowIcon />
                          </div>
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <div
        ref={scrollerRef}
        className={clsx(
          styles.scroller,
          phase !== "hold" &&
            (narrow ? styles.scrollerInactive : styles.scrollerHidden),
        )}
        // Deck document-lock yields to this during hold (title→intro return).
        data-hub-nested-scroll=""
        onScroll={scheduleMeasure}
        onClick={onNoteClick}
      >
        <div
          className={clsx(
            styles.scrollInner,
            needsScroll && styles.scrollInnerLong,
          )}
        >
          <NoteBlocksColumn
            blocks={blocks}
            animateOutlines={phase === "hold"}
            scrollRoot={scrollerRef}
            columnRef={columnRef}
            armedState={armedState}
            cue={
              <button
                type="button"
                className={clsx(
                  styles.flowCue,
                  gated && styles.flowCueGated,
                  showFade && styles.cueHeld,
                )}
                aria-label="Continue"
                aria-hidden={showFade || undefined}
                tabIndex={showFade ? -1 : undefined}
                onClick={continueNote}
              >
                <ArrowIcon />
              </button>
            }
          />
          {needsScroll ? <div ref={tailRef} className={styles.scrollTail} /> : null}
        </div>
      </div>
      {showFade ? (
        <div className={styles.fade}>
          <button
            type="button"
            className={clsx(
              styles.flowCue,
              styles.fadeCue,
              gated && styles.flowCueGated,
            )}
            aria-label="Continue"
            onClick={continueNote}
          >
            <ArrowIcon />
          </button>
        </div>
      ) : null}
    </div>
  );

  // Body portal so the squares stack above the nav. The stage's stacking
  // context cannot. First paint stays in-tree so hydration matches.
  if (portalRoot) {
    return createPortal(layer, portalRoot);
  }
  return layer;
};

export default IntroCovers;
